import { motion, useReducedMotion } from 'framer-motion';
import { Lock, CheckCircle, ChevronRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';
import ProgressRing from './ProgressRing';

/**
 * status: 'not_started' | 'in_progress' | 'completed' | 'locked'
 */
export default function ModuleCard({ module, onClick }) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();

  // Backend returns resolved language title via lang param; fallback to _tr/_en for mock compat
  const title = module.title || (lang === 'tr' ? module.title_tr : module.title_en) || '';
  const prereqTitle = module.prerequisiteTitle
    || (lang === 'tr' ? module.prerequisiteTitle_tr : (module.prerequisiteTitle_en || module.prerequisiteTitle_tr))
    || '';

  const isLocked = module.status === 'locked';

  const cardVariants = {
    initial: { scale: 1, y: 0, boxShadow: '0 4px 0 0 #E5E5E5' },
    hover: shouldReduce
      ? {}
      : { scale: 1.02, y: -2, boxShadow: '0 8px 24px -4px rgba(0,0,0,.12)' },
    tap: shouldReduce ? {} : { scale: 0.98, y: 2, boxShadow: '0 0px 0 0 #E5E5E5' },
  };

  const ctaKey = {
    not_started: 'cabin.module.cta.start',
    in_progress:  'cabin.module.cta.continue',
    completed:    'cabin.module.cta.retry',
    locked:       'cabin.module.cta.locked',
  }[module.status];

  const statusColor = {
    not_started: 'text-[var(--brand-primary)]',
    in_progress:  'text-[var(--brand-primary)]',
    completed:    'text-[var(--brand-success)]',
    locked:       'text-gray-400',
  }[module.status];

  const a11yLabel = isLocked
    ? interpolate(t('cabin.a11y.module_card_locked'), {
        order: module.order,
        title,
        prerequisite: prereqTitle || '',
      })
    : interpolate(t('cabin.a11y.module_card'), {
        order: module.order,
        title,
        progress: module.progress,
      });

  const cardContent = (
    <motion.div
      variants={cardVariants}
      initial="initial"
      whileHover={isLocked ? {} : 'hover'}
      whileTap={isLocked ? {} : 'tap'}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      onClick={isLocked ? undefined : onClick}
      className={`
        relative rounded-2xl overflow-hidden border-2 border-gray-200
        ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        bg-white
      `}
      role={isLocked ? 'img' : 'button'}
      aria-label={a11yLabel}
      aria-disabled={isLocked}
      tabIndex={isLocked ? -1 : 0}
      onKeyDown={(e) => {
        if (!isLocked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      data-testid={`cabin-module-card-${module.slug}`}
      style={{ boxShadow: '0 4px 0 0 #E5E5E5' }}
    >
      {/* Gradient header */}
      <div className={`h-24 bg-gradient-to-br ${module.gradient || 'from-sky-500 to-blue-600'} flex items-center justify-center relative`}>
        <span className="text-4xl" aria-hidden="true">{module.emoji || '📚'}</span>

        {/* Status overlay icon */}
        {module.status === 'completed' && (
          <div className="absolute top-2 right-2 bg-white/90 rounded-full p-0.5">
            <CheckCircle size={18} className="text-[var(--brand-success)]" aria-hidden="true" />
          </div>
        )}
        {isLocked && (
          <div className="absolute inset-0 bg-gray-900/30 flex items-center justify-center">
            <Lock size={24} className="text-white" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="p-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-1">
          <p className="text-sm font-bold text-[var(--text-main)] leading-tight line-clamp-2 flex-1">
            {title}
          </p>
          {module.status === 'in_progress' && (
            <div className="shrink-0">
              <ProgressRing
                progress={module.progress}
                size={32}
                strokeWidth={3}
              />
            </div>
          )}
        </div>

        {/* Progress bar for in_progress */}
        {module.status === 'in_progress' && (
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--brand-primary)] rounded-full transition-all duration-500"
              style={{ width: `${module.progress}%` }}
            />
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center justify-between">
          {module.milesReward != null && module.milesReward > 0 && (
            <span className="text-xs text-[var(--text-muted)]">
              {interpolate(t('cabin.module.miles_reward'), { miles: module.milesReward })}
            </span>
          )}
          {!isLocked && (
            <span className={`text-xs font-semibold flex items-center gap-0.5 ${statusColor}`}>
              {t(ctaKey)} <ChevronRight size={12} aria-hidden="true" />
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );

  if (isLocked && prereqTitle) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px] text-center">
            {interpolate(t('cabin.module.locked_tooltip'), { prerequisite: prereqTitle })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardContent;
}
